import LogRocket from 'logrocket';
import template from './stream.html';
import _ from 'lodash';

const commonARs = new Set(['16:9', '4:3']);
const SUPPORTED_H264_PROFILES = [
  'Baseline',
  'Main',
  'High'
];

const BITRATE_RANGE = {
  Min: 500,
  Max: 16000
};

/* @ngInject */
function StreamController($q, CameraService, growl, $scope) {
  const vm = this;

  // ///////////////// //
  // PUBLIC VARIABLES //
  // ///////////////// //

  vm.streams = _.reduce(
    ['live', 'event', 'recording'],
    (acc, type) => _.set(acc, type, { type, title: _.startCase(type) }),
    {}
  );
  vm.activeStream = vm.streams.live;

  vm.H264ProfileOptions = [...SUPPORTED_H264_PROFILES];

  vm.hasRotation = () => _.isFinite(parseInt(vm.camera?.rotation, 10));
  vm.hasBitrateLimit = () => _.has(vm.activeStream, ['settings', 'bitratelimit']);

  // //////////////// //
  // PUBLIC FUNCTIONS //
  // //////////////// //
  vm.$onChanges = onChanges;
  vm.toggleStream = toggleStream;
  vm.saveSettings = saveSettings;
  vm.updateH264ProfileSelection = updateH264ProfileSelection;
  vm.updateResolutionSelection = updateResolutionSelection;

  function onChanges({ liveSettings: live, eventSettings: event, recordingSettings: recording }) {
    _.forOwn({ live, event, recording }, (settingsChange, stream) => {
      if (_.has(settingsChange, 'currentValue')) {
        vm.streams[stream].settings = settingsChange.currentValue;
      }
    });
    setResolutionOptions();
    setH264ProfileOptions();
    setBitrateLimitOptions();
  }

  function toggleStream(type) {
    vm.activeStream = vm.streams[type];
    setResolutionOptions();
    setH264ProfileOptions();
    setBitrateLimitOptions();
  }

  function saveSettings(type) {
    const { title } = vm.streams[type];
    let growlMessage = growl.info(`Saving ${title} settings...`, {
      disableCloseButton: true
    });

    const savePromises = [];

    switch (type) {
      case 'live':
        savePromises.push(saveLiveSettings(), saveRotation());
        break;
      case 'event':
        savePromises.push(saveEventSettings());
        break;
      case 'recording':
        savePromises.push(saveRecordingSettings());
        break;
      default:
        break;
    }

    $q.all(savePromises)
      .then(() => {
        growlMessage.destroy();
        growlMessage = growl.success(`${title} settings saved successfully!`);
      })
      .catch((res) => {
        growlMessage.destroy();
        const error = `Error saving ${title} settings`;
        growlMessage = growl.error(res.error, { title: error });
        LogRocket.track(error);
      });
  }

  function updateH264ProfileSelection(selection) {
    $scope.$apply(() => {
      vm.activeStream.settings.h264profile = selection;
    });
  }

  function updateResolutionSelection(selection) {
    $scope.$apply(() => {
      vm.activeStream.settings.resolution = selection;
    });
  }

  // ///////////////// //
  // PRIVATE FUNCTIONS //
  // ///////////////// //

  function setResolutionOptions() {
    const resolutions = vm.activeStream?.settings?.resolutions || vm.resolutions;
    vm.resolutionOptions = groupResolutionsByAspectRatio(resolutions);
  }

  function setH264ProfileOptions() {
    let supportedProfiles = vm.activeStream?.settings?.options?.H264ProfilesSupported || [...SUPPORTED_H264_PROFILES];

    // ONVIF seems to report the highest supported profile from the following enum list
    // enum { 'Baseline', 'Main', 'Extended', 'High' }
    if (_.isString(supportedProfiles)) {
      // create array by working down list until highest supported found
      supportedProfiles = _.dropRightWhile(SUPPORTED_H264_PROFILES, prof => prof !== supportedProfiles);
      // Axis doesn't support 'Extended', and not sure if we want to either, but if a device happens to report something
      // other than what is in 'our list', resulting in an empty array, default to 'our list' (and cross fingers)
      if (_.isEmpty(supportedProfiles)) {
        supportedProfiles.push(...SUPPORTED_H264_PROFILES);
      }
    }

    // casing doesn't always match between some cameras profile setting and options array
    // this normalizes it so that the option dropdown correctly displays set option
    const capitalizedProfile = _.capitalize(vm.activeStream?.settings?.h264profile);
    if (capitalizedProfile && _.includes(supportedProfiles, capitalizedProfile)) {
      vm.activeStream.settings.h264profile = capitalizedProfile;
    }

    vm.H264ProfileOptions = supportedProfiles;
  }

  function setBitrateLimitOptions() {
    if (_.isString(vm.activeStream?.settings?.bitratelimit)) {
      vm.activeStream.settings.bitratelimit = _.toNumber(vm.activeStream.settings.bitratelimit);
    }
    const { Min: min, Max: max } = vm.activeStream?.settings?.options?.BitrateRange || BITRATE_RANGE;
    // make sure they are numbers so angularjs doesn't throw erros
    vm.bitrateRange = {
      min: _.toNumber(min),
      max: _.toNumber(max)
    };
  }

  function groupResolutionsByAspectRatio(resolutions = []) {
    const groups = resolutions.reduce((acc, resolution) => {
      const [width, height] = resolution.split('x').map(Number);
      const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
      const divider = gcd(width, height);
      const arWidth = width / divider;
      const arHeight = height / divider;
      const aspectRatio = `${arWidth}:${arHeight}`;

      if (!acc[aspectRatio]) {
        acc[aspectRatio] = {
          label: `${aspectRatio}${aspectRatio === vm.defaultAspectRatio ? ' (native)' : ''}`,
          value: aspectRatio,
          arWidth,
          arHeight,
          items: []
        };
      }

      acc[aspectRatio].items.push({ label: resolution, value: resolution });
      return acc;
    }, {});

    return Object.values(groups)
      .sort((
        { arWidth: widthA = 0, arHeight: heightA = 0, label: arA } = {},
        { arWidth: widthB = 0, arHeight: heightB = 0, label: arB } = {}
      ) => {
        const aIsCommon = commonARs.has(arA);
        const bIsCommon = commonARs.has(arB);
        if ((aIsCommon || bIsCommon) && !(aIsCommon && bIsCommon)) {
          // if only 1 is in the commonARs list, sort it first
          return aIsCommon ? -1 : 1;
        }
        // if neither or both are common, sort by width then height
        return widthB - widthA || heightB - heightA;
      });
  }

  function saveLiveSettings() {
    return CameraService.saveLiveSettings(vm.camera.id, vm.liveSettings)
      .then(() => {
        if (_.isFunction(vm.onLiveSettingsUpdate)) {
          vm.onLiveSettingsUpdate({ liveSettings: _.cloneDeep(vm.liveSettings) });
        }
      });
  }

  function saveEventSettings() {
    return CameraService.saveEventSettings(vm.camera.id, vm.eventSettings)
      .then(({ data }) => data);
  }

  function saveRecordingSettings() {
    return CameraService.saveRecordingSettings(vm.camera.id, vm.recordingSettings)
      .then(({ data }) => data);
  }

  function saveRotation() {
    return CameraService.saveRotation(vm.camera.id, vm.camera.rotation)
      .then(({ data }) => data);
  }
}

export default {
  name: 'stream',
  template,
  controllerAs: 'StreamCtrl',
  controller: StreamController,
  bindings: {
    camera: '<',
    resolutions: '<',
    liveSettings: '<',
    eventSettings: '<',
    recordingSettings: '<',
    defaultAspectRatio: '<?',
    onLiveSettingsUpdate: '&'
  }
};
